import { useEffect, useState } from 'react';
import { Dialog, Input, Radio } from '../../../components/primitives';
import { api, createCoordinatedDeployment, wordpressApi } from '../../../api';
import type { Environment, ProjectApplication, WordPressDeploymentPlanResponse } from '../../../api';
import { ErrorNotice, Field } from '../../../components/ui';
import { LevelBadge } from '../../../components/project/ui';

/**
 * WordPressDeployModal deploys one commit of the WordPress repository to one
 * environment. The plan is read before anything is created: which units
 * change, whether a database checkpoint, maintenance mode or a database
 * update is part of it — or why the deployment is not possible at all.
 */
export function WordPressDeployModal({
  projectId,
  environment,
  application,
  onClose,
  onCreated,
}: {
  projectId: string;
  environment: Environment;
  application: ProjectApplication | null;
  onClose: () => void;
  /** Called with the deployment's id, or its group's when no deployment came back. */
  onCreated: (target: { deployment?: string; group: string }) => void;
}) {
  const [source, setSource] = useState<'branch' | 'commit'>('branch');
  const [branch, setBranch] = useState(application?.default_branch || '');
  const [commit, setCommit] = useState('');
  const [resolved, setResolved] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [plan, setPlan] = useState<WordPressDeploymentPlanResponse | null>(null);
  const [planning, setPlanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);


  const sha = source === 'branch' ? resolved ?? '' : commit.trim().toLowerCase();

  // Resolve the branch to the commit it points at now, so the plan and the
  // deployment are about one exact commit.
  useEffect(() => {
    if (source !== 'branch' || !application || branch.trim() === '') {
      setResolved(null);
      return;
    }
    let alive = true;
    setResolving(true);
    const timer = window.setTimeout(() => {
      api
        .resolveApplicationBranch(projectId, application.id, branch.trim())
        .then((res) => alive && setResolved(res.commit_sha))
        .catch((err) => {
          if (!alive) return;
          setResolved(null);
          setError(err instanceof Error ? err : new Error(String(err)));
        })
        .finally(() => alive && setResolving(false));
    }, 400);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [source, branch, application, projectId]);

  useEffect(() => {
    setPlan(null);
    if (sha.length < 7) return;
    let alive = true;
    setPlanning(true);
    setError(null);
    wordpressApi
      .deploymentPlan(projectId, environment.id, sha)
      .then((res) => alive && setPlan(res))
      .catch((err) => alive && setError(err instanceof Error ? err : new Error(String(err))))
      .finally(() => alive && setPlanning(false));
    return () => {
      alive = false;
    };
  }, [sha, projectId, environment.id]);

  async function deploy() {
    if (!application || !plan?.possible) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createCoordinatedDeployment(projectId, {
        environmentId: environment.id,
        mode: 'live',
        scope: 'selected',
        start: true,
        components: [{ applicationId: application.id, branch: source === 'branch' ? branch.trim() : '', commitSha: sha }],
      });
      onCreated({ deployment: res.deployments[0]?.id, group: res.group.id });
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      size="md"
      className="wp-deploy"
      title={`Deploy to ${environment.name}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={deploy}
            disabled={busy || !plan?.possible || !application}
            data-testid="wp-deploy-submit"
          >
            {busy ? 'Starting' : 'Deploy'}
          </button>
        </>
      }
    >
      {!application?.repository_url ? (
        <p className="panel__note">No repository connected</p>
      ) : (
        <>
          <p className="muted">
            Repository <code>{application.repository_url}</code>
          </p>
          <fieldset className="wp-deploy__source">
            <legend className="visually-hidden">What to deploy</legend>
            <Radio
              name="wp-deploy-source"
              checked={source === 'branch'}
              onChange={() => setSource('branch')}
              label="The latest commit of a branch"
            />
            <Radio
              name="wp-deploy-source"
              checked={source === 'commit'}
              onChange={() => setSource('commit')}
              label="An exact commit"
            />
          </fieldset>
          {source === 'branch' ? (
            <Field
              label="Branch"
              hint={resolving ? 'Looking up the branch…' : resolved ? `Commit ${resolved.slice(0, 7)}` : undefined}
            >
              <Input value={branch} onChange={(event) => setBranch(event.target.value)} spellCheck={false} data-testid="wp-deploy-branch" />
            </Field>
          ) : (
            <Field label="Commit" hint="At least 7 characters">
              <Input value={commit} onChange={(event) => setCommit(event.target.value)} spellCheck={false} placeholder="a1b2c3d" />
            </Field>
          )}

          <div className="wp-deploy__plan" aria-live="polite" data-testid="wp-deploy-plan">
            {planning ? (
              <p className="muted">Planning…</p>
            ) : plan && !plan.possible ? (
              <p className="panel__note panel__note--danger">Not possible: {plan.reason}</p>
            ) : plan?.possible && plan.plan ? (
              <>
                <p className="wp-deploy__flags">
                  <LevelBadge level={plan.plan.database_checkpoint ? 'warning' : 'ok'}>
                    {plan.plan.database_checkpoint ? 'Database checkpoint first' : 'No database checkpoint needed'}
                  </LevelBadge>
                  <LevelBadge level={plan.plan.maintenance ? 'warning' : 'ok'}>
                    {plan.plan.maintenance ? 'Short maintenance window' : 'No maintenance mode'}
                  </LevelBadge>
                  {plan.plan.database_update ? <LevelBadge level="warning">Runs database updates</LevelBadge> : null}
                </p>
                <ol className="wp-deploy__steps">
                  {(plan.steps ?? []).map((step) => (
                    <li key={step.key}>
                      <strong>{step.title}</strong>
                      {step.description ? <span className="muted"> — {step.description}</span> : null}
                    </li>
                  ))}
                </ol>
                {(plan.plan.notes ?? []).map((note) => (
                  <p key={note} className="muted">
                    {note}
                  </p>
                ))}
                <p className="muted">
                  If the release fails its checks, the previous code is restored automatically when the rollback policy
                  allows it and no database update has run. The database is never rolled back automatically.
                </p>
              </>
            ) : null}
          </div>
        </>
      )}
      <ErrorNotice error={error} />
    </Dialog>
  );
}
